/**
 * The docket words, and the regions that say them differently (PAY-4262).
 *
 * A docket is rate based work: a submission to a form marked as a docket
 * creates Schedule of Rates transactions so the work can be invoiced. "Docket"
 * is the AU and NZ site word for it (day docket, plant docket). In the US and
 * Canada a docket is a court list, and the signed record of rate based work is
 * a "field ticket". So the DEFAULT of these three terms follows the account's
 * `region` setting, the same way the pay-cycle terms do (`./pay-cycle-terms`).
 * Nothing is stored unless someone types a value in Settings > Terminology,
 * and an explicit value wins over the region.
 *
 * They used to default to "Daily Record" (PAY-3445), which is the Site Diary's
 * name (`siteDiary`); a Site Diary can itself be docketed, so the two needed
 * different words.
 *
 * Pay Mobile bundles the same table (`pay-react-native`
 * `packages/i18n/src/locales/default-terms.ts`); change both together.
 *
 * Keep this module free of React and of other `@ci/*` packages, for the same
 * reason as `./pay-cycle-terms`.
 */
import type { AccountRegion } from './currency';

export const DOCKET_TERM_KEYS = ['docket', 'dockets', 'docketed'] as const;

export type DocketTermKey = (typeof DOCKET_TERM_KEYS)[number];

export type DocketTerms = Readonly<Record<DocketTermKey, string>>;

/** AU, NZ, EU, and any account that has never saved a region. */
export const DEFAULT_DOCKET_TERMS: DocketTerms = Object.freeze({
  docket: 'Docket',
  dockets: 'Dockets',
  docketed: 'Docketed',
});

const NORTH_AMERICAN_DOCKET_TERMS: DocketTerms = Object.freeze({
  docket: 'Field Ticket',
  dockets: 'Field Tickets',
  docketed: 'Ticketed',
});

const REGION_DOCKET_TERMS: Readonly<
  Partial<Record<AccountRegion, DocketTerms>>
> = {
  US: NORTH_AMERICAN_DOCKET_TERMS,
  CA: NORTH_AMERICAN_DOCKET_TERMS,
};

/**
 * Only the region's overrides, or an empty map for a region that has none.
 * This goes UNDER the account's own terminology, never over it.
 *
 * @param region - the PARSED region (`parseRegionSetting` of the stored
 *   setting).
 */
export function regionDocketTerms(
  region: AccountRegion | null,
): Partial<DocketTerms> {
  return (region ? REGION_DOCKET_TERMS[region] : undefined) ?? {};
}

/**
 * What the three terms read for a region with nothing saved against them. This
 * is the wording an empty settings field falls back to.
 */
export function docketTermsForRegion(
  region: AccountRegion | null,
): DocketTerms {
  return { ...DEFAULT_DOCKET_TERMS, ...regionDocketTerms(region) };
}
